import { reportsService } from "@/api/reports";
import { teamService } from "@/api/teams";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { LeaveTypeEnum, Role } from "@/types";
import { formatLeaveType } from "@/utils/leaveType";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import { useState } from "react";
import { Navigate } from "react-router-dom";

export default function Report() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState<number>(currentYear);
  const [month, setMonth] = useState<number | undefined>(undefined); // 0-11 or undefined
  const [teamId, setTeamId] = useState<string | undefined>(undefined);
  const [leaveType, setLeaveType] = useState<string | undefined>(undefined);

  // Pagination & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 2;

  // Access Control
  const hasAccess =
    !isAuthLoading && user
      ? user.roles
          .map((r) => r.toUpperCase())
          .some((r) => ([Role.ADMIN, Role.HR] as string[]).includes(r))
      : true; // Wait for auth or assume true until checked

  // Common Query Params
  const queryParams = {
    year,
    month: month !== undefined ? month + 1 : undefined, // API expects 1-12
    teamId: teamId === "all" ? undefined : teamId,
    leaveType: leaveType === "all" ? undefined : leaveType,
  };

  // Queries
  const { data: teams = [] } = useQuery({
    queryKey: ["teams"],
    queryFn: teamService.getAll,
  });

  const { data: leaveSummary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["reports", "summary", queryParams],
    queryFn: () => reportsService.getLeaveSummary(queryParams),
  });

  const { data: employeeUsage = [], isLoading: isEmployeeLoading } = useQuery({
    queryKey: ["reports", "employeeUsage", queryParams],
    queryFn: () => reportsService.getEmployeeLeaveUsage(queryParams),
  });

  const { data: monthlyTrends = [], isLoading: isTrendsLoading } = useQuery({
    queryKey: ["reports", "monthlyTrends", queryParams],
    queryFn: () => reportsService.getMonthlyTrends(queryParams),
  });

  const { data: teamUsage = [], isLoading: isTeamLoading } = useQuery({
    queryKey: ["reports", "teamUsage", queryParams],
    queryFn: () => reportsService.getTeamLeaveUsage(queryParams),
  });

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = Array.from({ length: 3 }, (_, i) => currentYear - i);

  // Filter and Pagination Logic
  const filteredEmployees = [...(employeeUsage || [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((emp) => {
      if (searchTerm.length > 3) {
        return emp.name.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    });

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  if (!hasAccess && !isAuthLoading) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="text-3xl font-bold text-foreground">
              Leaves Report
            </h2>
            <p className="text-muted-foreground mt-1">
              Comprehensive view of leave metrics and usage
            </p>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">Filters</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Year Selector */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Year</label>
                  <Select
                    value={year.toString()}
                    onValueChange={(val) => setYear(parseInt(val))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Month Selector */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Month</label>
                  <Select
                    value={month !== undefined ? month.toString() : "all"}
                    onValueChange={(val) =>
                      setMonth(val === "all" ? undefined : parseInt(val))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Months" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Months</SelectItem>
                      {months.map((m, idx) => (
                        <SelectItem key={idx} value={idx.toString()}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Team Selector */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Team</label>
                  <Select
                    value={teamId || "all"}
                    onValueChange={(val) =>
                      setTeamId(val === "all" ? undefined : val)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Teams" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Teams</SelectItem>
                      {teams.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Leave Type Selector */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Leave Type</label>
                  <Select
                    value={leaveType || "all"}
                    onValueChange={(val) =>
                      setLeaveType(val === "all" ? undefined : val)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {Object.values(LeaveTypeEnum).map((type) => (
                        <SelectItem key={type} value={type}>
                          {formatLeaveType(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leave Summary Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-blue-50/50 dark:bg-blue-900/10">
            <CardHeader className="pb-2">
              <CardDescription>Total Requests</CardDescription>
              <CardTitle className="text-3xl text-blue-600">
                {isSummaryLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  leaveSummary?.totalRequests || 0
                )}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="bg-green-50/50 dark:bg-green-900/10">
            <CardHeader className="pb-2">
              <CardDescription>Approved</CardDescription>
              <CardTitle className="text-3xl text-green-600">
                {isSummaryLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  leaveSummary?.approved || 0
                )}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="bg-yellow-50/50 dark:bg-yellow-900/10">
            <CardHeader className="pb-2">
              <CardDescription>Pending</CardDescription>
              <CardTitle className="text-3xl text-yellow-600">
                {isSummaryLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  leaveSummary?.pending || 0
                )}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="bg-red-50/50 dark:bg-red-900/10">
            <CardHeader className="pb-2">
              <CardDescription>Rejected</CardDescription>
              <CardTitle className="text-3xl text-red-600">
                {isSummaryLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  leaveSummary?.rejected || 0
                )}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="bg-gray-50/50 dark:bg-gray-800/10">
            <CardHeader className="pb-2">
              <CardDescription>Cancelled</CardDescription>
              <CardTitle className="text-3xl text-gray-600">
                {isSummaryLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  leaveSummary?.cancelled || 0
                )}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Two Column Layout: Monthly Trends & Team Usage */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Trends */}
          <Card>
            <CardHeader>
              <CardTitle>Monthly Trends</CardTitle>
              <CardDescription>
                Request volume over{" "}
                {month !== undefined ? "selected month" : "the year"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-[300px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Approved</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isTrendsLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-4">
                          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : monthlyTrends.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center py-4 text-muted-foreground"
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      monthlyTrends.map((trend, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">
                            {trend.month}
                          </TableCell>
                          <TableCell className="text-right">
                            {trend.totalRequests}
                          </TableCell>
                          <TableCell className="text-right text-green-600 font-medium">
                            {trend.approved}
                          </TableCell>
                          <TableCell className="text-right text-yellow-600 font-medium">
                            {trend.pending}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Team Usage */}
          <Card>
            <CardHeader>
              <CardTitle>Team Leave Usage</CardTitle>
              <CardDescription>Leave distribution by team</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-[300px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team</TableHead>
                      <TableHead className="text-right">Employees</TableHead>
                      <TableHead className="text-right">Leaves Taken</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isTeamLoading ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-4">
                          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : teamUsage.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="text-center py-4 text-muted-foreground"
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      teamUsage.map((team, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">
                            {team.teamName}
                          </TableCell>
                          <TableCell className="text-right">
                            {team.totalEmployees}
                          </TableCell>
                          <TableCell className="text-right">
                            {team.totalLeavesTaken}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Employee Leave Usage (Full Width) */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle>Employee Leave Usage</CardTitle>
                <CardDescription>
                  Detailed leave usage per employee
                </CardDescription>
              </div>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search employee..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to page 1 on search
                  }}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead className="text-right">Allocated</TableHead>
                    <TableHead className="text-right">Used</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isEmployeeLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : paginatedEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No employee data found matching filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedEmployees.flatMap((employee) =>
                      employee.leaveUsage.map((usage, idx) => (
                        <TableRow key={`${employee.employeeId}-${idx}`}>
                          <TableCell className="font-medium">
                            {employee.name}
                          </TableCell>
                          <TableCell>{employee.team}</TableCell>
                          <TableCell>
                            {formatLeaveType(usage.leaveType)}
                          </TableCell>
                          <TableCell className="text-right">
                            {usage.allocated}
                          </TableCell>
                          <TableCell className="text-right font-medium text-blue-600">
                            {usage.used}
                          </TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {usage.remaining}
                          </TableCell>
                        </TableRow>
                      )),
                    )
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination Controls */}
            {!isEmployeeLoading && filteredEmployees.length > 0 && (
              <div className="flex items-center justify-end space-x-2 py-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="text-sm font-medium">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
